import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import "maplibre-gl/dist/maplibre-gl.css";
import maplibre from "maplibre-gl";
import { getSignedS3Url } from "../../../common/helpers";
import appConfig from "../../../common/appConfig";

const MapContainer = styled.div<{ height?: number }>`
  width: 100%;
  min-height: ${(p) => (p.height ? p.height : 500)}px;
`;

export const MapContext = React.createContext<maplibre.Map | null>(null);
export const MapLoadedContext = React.createContext<boolean>(false);

const Map = (props: {
  styleUrl: string;
  height?: number;
  center: [number, number];
  children?: React.ReactNode[] | React.ReactNode;
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<maplibre.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const { styleUrl, height, center, children } = props;

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }
    const map = new maplibre.Map({
      container: mapContainerRef.current,
      center: center,
      zoom: 9,
      style: styleUrl,
      transformRequest: (url, resourceType) => {
        // Sign aws (s3) urls
        if (url.includes(`s3.${appConfig.awsUrlSuffix}`)) {
          const bucketAndKey = url.replace(/^https?:\/\//, "");
          const parts = bucketAndKey.split("/");
          const bucket = (parts.shift() || "").split(".")[0];
          const key = parts.join("/") || "";
          return {
            url: getSignedS3Url(bucket, key),
          };
        }
        // Don't sign other urls
        return { url };
      },
    });

    // Helper for debugging map issues
    if (process.env.NODE_ENV === "development") {
      //@ts-ignore
      if (!window.MAPS) {
        //@ts-ignore
        window.MAPS = [];
      }
      //@ts-ignore
      window.MAPS.push(map);
    }

    setMapLoaded(false);
    setMap(map);
  }, [styleUrl, center]);

  useEffect(() => {
    if (!map) {
      return;
    }

    map.on("load", function () {
      setMapLoaded(true);
    });
  }, [map]);

  return (
    <MapContext.Provider value={map}>
      <MapLoadedContext.Provider value={mapLoaded}>
        <MapContainer ref={mapContainerRef} height={height}></MapContainer>
        {children}
      </MapLoadedContext.Provider>
    </MapContext.Provider>
  );
};
export default Map;
